import { createTheme } from '@mui/material/styles';

// Process Control Modern Theme
// Colors from Logo: Golden Yellow #F5A623, Dark Charcoal #3D3D3D
const processControlTheme = createTheme({
  palette: {
    primary: {
      main: '#F5A623', // Golden Yellow from logo
      light: '#FFBE45',
      dark: '#D88F1A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#3D3D3D', // Dark Charcoal from logo
      light: '#5A5A5A',
      dark: '#2A2A2A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2D2D2D',
      secondary: '#5A5A5A',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    error: {
      main: '#DC3545',
      light: '#E57373',
      dark: '#C62828',
    },
    success: {
      main: '#28A745',
      light: '#81C784',
      dark: '#1B5E20',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    h1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 400,
      lineHeight: 1.7,
      fontSize: '1rem',
    },
    body2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 400,
      lineHeight: 1.6,
      fontSize: '0.875rem',
    },
    button: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      fontSize: '0.75rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.04)',
    '0 2px 4px rgba(0, 0, 0, 0.06)',
    '0 4px 8px rgba(0, 0, 0, 0.06)',
    '0 6px 12px rgba(0, 0, 0, 0.08)',
    '0 8px 16px rgba(0, 0, 0, 0.08)',
    '0 12px 24px rgba(0, 0, 0, 0.1)',
    '0 16px 32px rgba(0, 0, 0, 0.1)',
    '0 20px 40px rgba(0, 0, 0, 0.12)',
    '0 24px 48px rgba(0, 0, 0, 0.12)',
    '0 28px 56px rgba(0, 0, 0, 0.14)',
    '0 32px 64px rgba(0, 0, 0, 0.14)',
    '0 36px 72px rgba(0, 0, 0, 0.16)',
    '0 40px 80px rgba(0, 0, 0, 0.16)',
    '0 44px 88px rgba(0, 0, 0, 0.18)',
    '0 48px 96px rgba(0, 0, 0, 0.18)',
    '0 52px 104px rgba(0, 0, 0, 0.2)',
    '0 56px 112px rgba(0, 0, 0, 0.2)',
    '0 60px 120px rgba(0, 0, 0, 0.22)',
    '0 64px 128px rgba(0, 0, 0, 0.22)',
    '0 68px 136px rgba(0, 0, 0, 0.24)',
    '0 72px 144px rgba(0, 0, 0, 0.24)',
    '0 76px 152px rgba(0, 0, 0, 0.26)',
    '0 80px 160px rgba(0, 0, 0, 0.26)',
    '0 84px 168px rgba(0, 0, 0, 0.28)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          backgroundColor: '#FAFAFA',
          color: '#2D2D2D',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        '@keyframes slideUp': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '@keyframes slideInLeft': {
          from: {
            opacity: 0,
            transform: 'translateX(-30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes slideInRight': {
          from: {
            opacity: 0,
            transform: 'translateX(30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes scaleIn': {
          from: {
            opacity: 0,
            transform: 'scale(0.95)',
          },
          to: {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          background: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '0.95rem',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          backgroundColor: '#F5A623',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#D88F1A',
            boxShadow: '0 4px 16px rgba(245, 166, 35, 0.4)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedSecondary: {
          backgroundColor: '#3D3D3D',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#2A2A2A',
            boxShadow: '0 4px 16px rgba(61, 61, 61, 0.4)',
          },
        },
        outlined: {
          borderColor: '#F5A623',
          borderWidth: 2,
          color: '#F5A623',
          '&:hover': {
            borderColor: '#D88F1A',
            backgroundColor: 'rgba(245, 166, 35, 0.08)',
            borderWidth: 2,
          },
        },
        outlinedSecondary: {
          borderColor: '#3D3D3D',
          borderWidth: 2,
          color: '#3D3D3D',
          '&:hover': {
            borderColor: '#2A2A2A',
            backgroundColor: 'rgba(61, 61, 61, 0.08)',
            borderWidth: 2,
          },
        },
        text: {
          color: '#F5A623',
          '&:hover': {
            backgroundColor: 'rgba(245, 166, 35, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: '#E0E0E0',
              borderWidth: 1.5,
              transition: 'all 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: '#BDBDBD',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F5A623',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#F5A623',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(245, 166, 35, 0.12)',
          color: '#D88F1A',
          '&:hover': {
            backgroundColor: 'rgba(245, 166, 35, 0.2)',
          },
        },
        outlined: {
          borderColor: '#F5A623',
          color: '#F5A623',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#F5A623',
          textDecoration: 'none',
          fontWeight: 500,
          transition: 'color 0.2s ease',
          '&:hover': {
            color: '#D88F1A',
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(245, 166, 35, 0.1)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(245, 166, 35, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(245, 166, 35, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(245, 166, 35, 0.16)',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          '&.Mui-selected': {
            color: '#F5A623',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#F5A623',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          color: '#1B5E20',
        },
        standardError: {
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          color: '#C62828',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 166, 35, 0.1)',
          color: '#D88F1A',
        },
        standardInfo: {
          backgroundColor: 'rgba(61, 61, 61, 0.1)',
          color: '#3D3D3D',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#3D3D3D',
          borderRadius: 8,
          fontSize: '0.8rem',
          padding: '8px 12px',
        },
        arrow: {
          color: '#3D3D3D',
        },
      },
    },
  },
});

// Export color constants for use in components
export const colors = {
  primary: '#F5A623',
  primaryLight: '#FFBE45',
  primaryDark: '#D88F1A',
  secondary: '#3D3D3D',
  secondaryLight: '#5A5A5A',
  secondaryDark: '#2A2A2A',
  background: '#FAFAFA',
  surface: '#FFFFFF',
  textPrimary: '#2D2D2D',
  textSecondary: '#5A5A5A',
  border: '#E0E0E0',
  divider: 'rgba(0, 0, 0, 0.08)',
  success: '#28A745',
  error: '#DC3545',
  warning: '#F5A623',
};

export default processControlTheme;
